<template>
  <AsidePanel
    :components="components"
    :disableCta="shouldDisableCta"
    :handleMountedCallback="handleMountedCallback"
    :handleSave="handleSave"
    :isExpanded="isExpanded"
    :itemId="itemId"
    :itemType="itemType"
    :navigateToTab="activeTab"
    :redirectTo="`/managedSoftware/apple`"
    :viewport="viewport"
  >
    <template v-slot:avatar>
      <AsideIcon
        icon="apps"
      />
    </template>
  </AsidePanel>
</template>

<script>
import {
  ItemActionConsts, ItemTableConsts, ItemTypeConsts, StoreModelMap,
} from 'client/util/Constants';
import { mapActions, mapGetters } from 'vuex';
import AsideIcon from 'client/components/AsidePanel/AsideIcon.vue';
import AsidePanel from 'client/components/AsidePanel/AsidePanel.vue';
import AsidePanelPropsMixin from 'client/components/AsidePanel/mixins/AsidePanelPropsMixin';
import CustomAppsDetails from 'client/SoftwareApps/CustomAppsDetails.vue';
import ItemType from 'client/util/Constants/ItemType';
import ModalEventBus from 'components/Modal/ModalEventBus';
import SaveReturnValue from 'components/AsidePanel/SaveReturnValue';
import SoftwareAppsStatusItemTable from 'client/SoftwareApps/SoftwareAppsStatusItemTable';
import SystemGroupItemTable from 'components/ItemTable/SystemGroupItemTable';
import SystemItemTable from 'client/systems/SystemItemTable/SystemItemTable';

const { create, update } = ItemActionConsts;
const { DeviceGroupItemType } = ItemType;
const { softwareApps } = ItemTypeConsts;
const deviceGroupItem = new DeviceGroupItemType();
const softwareAppsModel = StoreModelMap[softwareApps];

export default {
  name: 'CustomAppsAsidePanel',

  components: {
    AsidePanel,
    AsideIcon,
  },

  mixins: [
    AsidePanelPropsMixin,
  ],

  ModalEventBus,

  props: {
    itemType: {
      type: String,
      default: softwareApps,
    },
    navigateToTab: {
      type: Object,
      default: () => {},
      validator(obj) {
        if (obj) {
          return typeof obj.tabLabel !== 'undefined';
        }
        return true;
      },
    },
  },

  data() {
    return {
      activeTab: this.navigateToTab,
      components: [
        {
          label: 'Details',
          vueComponent: CustomAppsDetails,
        },
        {
          itemType: ItemTypeConsts.systemGroup,
          label: 'Device Groups',
          headerLabels: {
            [create]: 'Deploy to the following device groups',
            [update]: ' is deployed to the following device groups',
          },
          vueComponent: SystemGroupItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/softwareapps/${this.itemId}/systemgroups` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.softwareAppsAsidePanel,
          },
        },
        {
          itemType: ItemTypeConsts.system,
          label: 'Devices',
          headerLabels: {
            [create]: 'Deploy to the following devices',
            [update]: 'is deployed on the following devices',
          },
          vueComponent: SystemItemTable,
          vueComponentProps: {
            boundItemsUrl: this.itemId ? `/api/v2/softwareapps/${this.itemId}/systems` : undefined,
            inDetailsAsidePanel: true,
            panelType: ItemTableConsts.panelType.softwareAppsAsidePanel,
            parentOS: ItemTableConsts.parentOSType.darwin,
          },
        },
        {
          label: 'Status',
          vueComponent: SoftwareAppsStatusItemTable,
          vueComponentProps: {
            isAppleCustomApp: true,
            isAppleVpp: false,
            softwareAppsId: this.itemId,
          },
        },
      ],
      disableCtaButton: false,
      errorMessage: 'Please review the Details Tab for missing or invalid information',
      itemHasBeenSaved: false,
    };
  },

  computed: {
    ...mapGetters(softwareAppsModel, [
      'displayName',
      'getModifiedAssociations',
      'hasItemChanged',
      'haveAssociationsChanged',
      'isItemBeingCreated',
    ]),

    shouldDisableCta() {
      return !(this.hasItemChanged || this.haveAssociationsChanged) || this.isItemBeingCreated;
    },
  },

  methods: {
    ...mapActions(softwareAppsModel, [
      'saveItem',
      'bindSoftwareAppToSystems',
      'bindSoftwareAppToSystemGroups',
    ]),

    confirmBindings() {
      const bindings = this.getModifiedAssociations;
      const mdTemplate = this.getBindingsModalBodyText(bindings);

      return new Promise((resolve) => {
        this.$options.ModalEventBus.open({
          component: 'ConfirmationModal',
          componentProps: {
            mdTemplate,
            headerText: 'Managed Software Associations',
            continueText: 'save',
            handleContinue: () => {
              this.$options.ModalEventBus.close();
              resolve(true);
            },
            handleCancel: () => {
              this.$options.ModalEventBus.close();
              resolve(false);
            },
          },
        });
      });
    },

    getBindingsModalBodyText(bindings) {
      const {
        systemsToBind,
        systemGroupsToBind,
        systemsToUnbind,
        systemGroupsToUnbind,
      } = bindings;
      let boundText = '';
      let unboundText = '';

      const deviceGroupLabel = `${deviceGroupItem.label}(s)`;

      if (systemsToBind.length && systemGroupsToBind.length) {
        boundText = `The managed software **${this.displayName}** will be installed on **${systemsToBind.length} macOS devices** directly and to **macOS devices** in **${systemGroupsToBind.length}** ${deviceGroupLabel}.`;
      } else {
        boundText = systemsToBind.length ? `The managed software **${this.displayName}** will be installed on **${systemsToBind.length} macOS devices**.` : boundText;
        boundText = systemGroupsToBind.length ? `The managed software **${this.displayName}** will be installed on **macOS devices** in **${systemGroupsToBind.length}** ${deviceGroupLabel}.` : boundText;
      }
      boundText = boundText ? `${boundText}\n\nAfter installation, the software will be managed by JumpCloud.\n\n*Tip: Perform a test installation of managed software before you bind the software to your entire fleet.*\n\n` : boundText;

      if (systemsToUnbind.length && systemGroupsToUnbind.length) {
        unboundText = `The managed software **${this.displayName}** will no longer be managed on **${systemsToUnbind.length} macOS devices** directly and on **macOS devices** in **${systemGroupsToUnbind.length}** ${deviceGroupLabel}.`;
      } else {
        unboundText = systemsToUnbind.length ? `The managed software **${this.displayName}** will no longer be managed on **${systemsToUnbind.length} macOS devices** directly.` : unboundText;
        unboundText = systemGroupsToUnbind.length ? `The managed software **${this.displayName}** will no longer be managed on **macOS devices** in **${systemGroupsToUnbind.length}** ${deviceGroupLabel}.` : unboundText;
      }
      unboundText = unboundText ? `${unboundText}\n\nThis action uninstalls the software from the ${deviceGroupLabel}.` : unboundText;

      return `${boundText}${unboundText}`;
    },

    handleMountedCallback() {
      if (this.navigateToTab) {
        this.navigateToAsideTab(this.navigateToTab.tabLabel);
      }
    },

    async handleSave() {
      const validator = this.$validator;
      await validator.validate();

      if (validator.errors.any()) {
        throw Error(this.errorMessage);
      }

      this.itemHasBeenSaved = true;

      if (this.haveAssociationsChanged) {
        const canContinue = await this.confirmBindings();

        if (canContinue === false) {
          return new SaveReturnValue({ canContinue });
        }
      }

      return this.saveItem()
        .then(() => this.bindSoftwareAppToSystemGroups())
        .then(() => this.bindSoftwareAppToSystems())
        .catch((error) => {
          throw error instanceof Error ? error : Error('Unknown error');
        });
    },

    hasModelChanged() {
      if (this.itemHasBeenSaved) {
        return false;
      }
      return this.hasItemChanged || this.haveAssociationsChanged;
    },

    navigateToAsideTab(tabLabel) {
      this.activeTab = { tabLabel };
    },
  },
};
</script>
